import React, {
	useEffect,
	useState,
	useRef,
	useMemo,
	useCallback,
} from "react";

import { CiCirclePlus } from "react-icons/ci";
import { MdOutlineMessage } from "react-icons/md";
import { MdOutlineMarkUnreadChatAlt } from "react-icons/md";
import { BellIcon } from "@heroicons/react/24/outline";
import { GoChevronUp, GoChevronDown } from "react-icons/go";
import AddGuestModal from "../../AddGuestModal/AddGuestModal";
import ChangeStatusModal from "../../ChangeStatusModal/ChangeStatusModal";
import UpdateGuestModal from "../../UpdateGuestModal/UpdateGuestModal";
import EditGuestModal from "../../EditGuestModal/EditGuestModal";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { toast } from "react-toastify";
import { baseUrl } from "../../../Environments/environment";
import { useAuth } from "../../../Contexts/AuthContext/AuthContext";
import {
	QueryClient,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";
import "./your-styles.css";
import { CiCalendarDate } from "react-icons/ci";
import ChangeStats from "../../../Assets/changeStatus.svg";
import Edit from "../../../Assets/edit.svg";
import MessageIcon from "../../../Assets/message.svg";
import { useProperty } from "../../../Contexts/PropertyContext/PropertyContext";
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner";

import checkInI from "../../../Assets/Checkin.svg";
import checkOutI from "../../../Assets/Checkout.svg";
import { useSocket } from "../../../Contexts/SocketContext/SocketContext";
import ChatScreenModal from "../Message/ChatScreenModal/ChatScreenModal";
import AddonRequestModal from "../../AddonRequestModal/AddonRequestModal";
import { apiClient } from "../../../api";
import { useSearchParams } from "react-router-dom";
import NoDataFound from "../../NoDataFound";
import ChatListModal from "../Message/ChatListModal/ChatListModal";
import { BiSolidMessageRoundedDetail } from "react-icons/bi";
import { useInfiniteQuery } from "@tanstack/react-query";

const statuses = [
	"All Bookings",
	"Check In",
	"All In House",
	"Check Out",
	"Checked Out",
];

const mapStatus = {
	"All Bookings": "Reservation",
	"Check In": "Reservation",
	"All In House": "In House",
	"Check Out": "In House",
	"Checked Out": "Checked Out",
};
const mapReservationStatus = {
	Cancelled: "Cancelled",
	Confirmed: "Booking",
	"No Show": "No Show",
};

const emptyStateMessages = {
	"All Bookings": "No bookings",
	"Check In": "No guests checking in",
	"All In House": "No in-house guests",
	"Check Out": "No guests checking out",
	"Checked Out": "No checked out guests",
};

const reverseMapStatus = {
	Reservation: "Booking",
	"In House": "In House",
	"Checked Out": "Checked Out",
	Cancelled: "Cancelled",
};
const convertToStatus = (status) => {
	if (status.currentStatus === "Reservation") {
		return mapReservationStatus[status.reservationStatus] || "Unknown";
	}
	return reverseMapStatus[status.currentStatus] || "Unknown";
};
dayjs.extend(utc);
dayjs.extend(timezone);
const useReactQuerySubscription = (
	socket,
	propertyId,
	currentStatus,
	selectedDate,
) => {
	const queryClient = useQueryClient();

	useEffect(() => {
		if (socket) {
			const updateGuests = () => {
				queryClient.removeQueries({ queryKey: ["guests"] }); // reset pages
				queryClient.invalidateQueries({ queryKey: ["guests"] }); // refetch
			};

			socket.on("guest:guestStatusUpdate", updateGuests);
			socket.on("guest:guestUpdate", updateGuests);

			return () => {
				socket.off("guest:guestStatusUpdate", updateGuests);
				socket.off("guest:guestUpdate", updateGuests);
			};
		}
	}, [socket, queryClient, propertyId, currentStatus, selectedDate]);
};

// const fetchGuests = async (
// 	propertyId,
// 	currentStatus,
// 	selectedDate,
// 	searchQuery,
// 	accessToken,
// 	todaysDate,
// ) => {
// 	try {
// 		const params = {
// 			...(currentStatus && { currentStatus: mapStatus[currentStatus] }),
// 			...(currentStatus === "Check In" && { checkIn: selectedDate }),
// 			...(currentStatus === "Check Out" && { checkOut: selectedDate }),
// 			...(searchQuery && { search: searchQuery }),
// 			todaysDate,
// 			selectedDate,
// 		};
//
// 		const response = await apiClient.get(`${baseUrl}/guest/${propertyId}`, {
// 			params: params,
// 		});
// 		if (response.ok) {
// 			const newToken = response.headers.get("Authorization");
// 			if (newToken) {
// 				localStorage.setItem("accessToken", newToken.split(" ")[1]); // Save the new token
// 			}
// 		}
//
// 		return response.data.result.guests;
// 	} catch (error) {
// 		toast.error("Failed to fetch guests. Please try again.");
// 		throw error;
// 	}
// };
const fetchGuests = async ({
	pageParam = 1,
	propertyId,
	currentStatus,
	selectedDate,
	searchQuery,
	todaysDate,
}) => {
	console.log(propertyId, currentStatus, selectedDate, searchQuery, todaysDate);
	const params = {
		...(currentStatus && { currentStatus: mapStatus[currentStatus] }),
		...(currentStatus === "Check In" && { checkIn: selectedDate }),
		...(currentStatus === "Check Out" && { checkOut: selectedDate }),
		...(searchQuery && { search: searchQuery }),
		todaysDate,
		selectedDate,
		page: pageParam,
		limit: 20,
	};

	const response = await apiClient.get(`${baseUrl}/guest/${propertyId}`, {
		params,
	});

	return response.data.result.guests; // IMPORTANT: full object
};
const renderStars = (rating) => {
	const full = Math.max(0, Math.min(5, Number(rating) || 0));
	return (
		<div className="flex items-center gap-1">
			{[...Array(5)].map((_, i) => (
				<span
					key={i}
					className={
						i < full ? "text-yellow-400 text-xl" : "text-gray-300 text-xl"
					}
				>
					★
				</span>
			))}
		</div>
	);
};

export default function Home() {
	const calendarRef = useRef(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isChangeStatusModalOpen, setIsChangeStatusModalOpen] = useState(false);
	const [isEditGuestModalOpen, setIsEditGuestModalOpen] = useState(false);
	const [currentStatus, setCurrentStatus] = useState("All Bookings");
	const [isUpdateGuestModalOpen, setIsUpdateGuestModalOpen] = useState(false);
	const [isAddonRequestModalOpen, setIsAddonRequestModalOpen] = useState(false);
	const { propertyChats } = useProperty();

	const [searchParams] = useSearchParams();
	const openModel = searchParams.get("openAddOnsRequest");
	useEffect(() => {
		if (openModel === "true") {
			setIsAddonRequestModalOpen(true);
		}
	}, [openModel]);

	const [showCheckInPendingGuests, setShowCheckInPendingGuests] =
		useState(true);
	const [pendingCheckInOutGuestsData, setPendingCheckInOutGuestsData] =
		useState([]);

	// const [guests, setGuests] = useState([]);
	const [guest, setGuest] = useState();
	const [selectedGuest, setSelectedGuest] = useState(null);
	const { selectedPropertyId, setPropertyChats } = useProperty();
	const { socket } = useSocket();
	const [searchQuery, setSearchQuery] = useState("");
	const [addOns, setAddOns] = useState([]);

	const { accessToken } = useAuth();
	const propertyId = selectedPropertyId;
	const [selectedDate, setSelectedDate] = useState(
		dayjs().format("YYYY-MM-DD"),
	);

	const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
	const [unCheckedAddonReq, setUncheckedAddonReq] = useState(0);
	const todayDate = dayjs().tz(dayjs.tz.guess()).format("YYYY-MM-DD");
	useEffect(() => {
		const handleNewAddon = (data) => {
			setUncheckedAddonReq((count) => count + data.count);
		};

		socket.on("addOn:newAddon", handleNewAddon);
		return () => {
			socket.off("addOn:newAddon", handleNewAddon);
		};
	}, [socket]);

	useEffect(() => {
		const handleClickOutside = (event) => {
			// Check if click is outside both the dropdown and the trigger button
			if (calendarRef.current && !calendarRef.current.contains(event.target)) {
				setIsDatePickerOpen(false);
			}
		};

		// Only add the event listener if the dropdown is open
		if (isDatePickerOpen) {
			document.addEventListener("click", handleClickOutside);
		}

		return () => {
			document.removeEventListener("click", handleClickOutside);
		};
	}, [isDatePickerOpen]);
	const fetchPendingCheckInOutGuests = async (
		propertyId,
		currentStatus,
		selectedDate,
		searchQuery,
		accessToken,
		todaysDate,
	) => {
		try {
			const params = {
				...(currentStatus && { currentStatus: mapStatus[currentStatus] }),
				...(currentStatus === "Check In" && { checkIn: selectedDate }),
				...(currentStatus === "Check Out" && { checkOut: selectedDate }),
				...(searchQuery && { search: searchQuery }),
				todaysDate,
				selectedDate,
			};

			const response = await apiClient.get(
				`${baseUrl}/guest/${propertyId}/pending`,
				{
					params: params,
				},
			);
			if (response.ok) {
				const newToken = response.headers.get("Authorization");
				if (newToken) {
					localStorage.setItem("accessToken", newToken.split(" ")[1]); // Save the new token
				}
			}
			return response.data.result.guests;
		} catch (error) {
			throw error;
		}
	};

	useEffect(() => {
		if (!socket) return;

		if (currentStatus === "Check In" || currentStatus === "Check Out") {
			const getPendingCheckInOutGuests = async () => {
				// existing logic
			};

			getPendingCheckInOutGuests();

			socket.on("guest:guestStatusUpdate", getPendingCheckInOutGuests);
			socket.on("guest:guestUpdate", getPendingCheckInOutGuests);

			return () => {
				socket.off("guest:guestStatusUpdate", getPendingCheckInOutGuests);
				socket.off("guest:guestUpdate", getPendingCheckInOutGuests);
			};
		}
	}, [socket, propertyId, currentStatus, selectedDate]);

	const [addOnLength, setAddOnLength] = useState(0);
	const fetchAddOns = async () => {
		try {
			const response = await apiClient.get(
				`${baseUrl}/guest/addons/${propertyId}?requestStatus=Requested`,
			);
			console.log(response);
			console.warn(response.data.result.requests);
			if (response.status == 200) {
				const newToken = response.headers.get("Authorization");
				if (newToken) {
					localStorage.setItem("accessToken", newToken.split(" ")[1]); // Save the new token
				}
				console.log("AdddOns -->>>>>>", response.data.result.requests);
				const requestedAddons = response.data.result.requests.filter(
					(addon) =>
						addon.guestId?.status?.currentStatus !== "Checked Out" &&
						addon.requestStatus === "Requested",
				);
				const tempAddons = requestedAddons;
				setAddOns(tempAddons);
				setAddOnLength(tempAddons.length);
			}
		} catch (error) {
			console.error("Error fetching requests:", error);
		}
	};

	useEffect(() => {
		console.log("USE EFFECT FETCH");
		fetchAddOns();
	}, []);

	const notFoundMessage = useMemo(() => {
		switch (currentStatus) {
			case "All Bookings":
				return "No guests found";
			case "Check In":
				return "No guests are currently checking in";
			case "All In House":
				return "No guests are in house currently";
			case "Check Out":
				return "No guests are currently checking out";
			case "Checked Out":
				return "No guested are checked out";

			default:
				break;
		}
	}, [currentStatus]);

	// useEffect(() => {
	//   console.log("HERE Helllooooo");
	//   console.warn(addOns);
	//   if (addOnLength !== addOns.length) {
	//     setAddOnLength(addOns.length);
	//   }
	// }, [addOns, isAddonRequestModalOpen]);

	const handleDateChange = (date) => {
		if (date) {
			const localDate = dayjs(date).tz(dayjs.tz.guess());
			const formattedDate = localDate.format("YYYY-MM-DD");

			setSelectedDate(formattedDate);
		} else {
			setSelectedDate(null);
		}
		setIsDatePickerOpen(false);
	};

	const getInitials = (firstName, lastName) => {
		return (firstName[0] + lastName[0]).toUpperCase();
	};

	const handleClick = (status) => {
		setCurrentStatus(status);
	};
	//  console.log("searach",searchQuery)
	useReactQuerySubscription(socket, propertyId, currentStatus, selectedDate);

	const {
		data,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isLoading,
		error,
	} = useInfiniteQuery({
		queryKey: ["guests", propertyId, currentStatus, selectedDate, searchQuery],
		queryFn: ({ pageParam = 1 }) =>
			fetchGuests({
				pageParam,
				propertyId,
				currentStatus,
				selectedDate,
				searchQuery,
				todaysDate: todayDate,
			}),
		getNextPageParam: (lastPage, pages) => {
			if (!lastPage.hasMore) return undefined;
			return pages.length + 1;
		},
	});
	const guests = data?.pages.flatMap((page) => page.data) || [];
	const observer = useRef();

	const lastGuestRef = useCallback(
		(node) => {
			if (isFetchingNextPage) return;

			if (observer.current) observer.current.disconnect();

			observer.current = new IntersectionObserver((entries) => {
				if (entries[0].isIntersecting && hasNextPage) {
					fetchNextPage();
				}
			});

			if (node) observer.current.observe(node);
		},
		[isFetchingNextPage, hasNextPage, fetchNextPage],
	);
	const openChatScreen = (guest) => {
		setSelectedGuest(guest);
	};

	const closeChatScreen = () => {
		setSelectedGuest(null);
	};

	const openUpdateGuestModal = () => {
		setIsUpdateGuestModalOpen(true);
	};

	// Function to close the Update Guest modal
	const closeUpdateGuestModal = () => {
		setIsUpdateGuestModalOpen(false);
	};

	// Function to open AddonRequestModal when bell icon is clicked
	const openAddonRequestModal = (e) => {
		e.stopPropagation();
		setIsAddonRequestModalOpen(true);
	};
	const fetchChatList = async (baseUrl, selectedPropertyId, accessToken) => {
		const response = await apiClient.get(
			`${baseUrl}/chatList/${selectedPropertyId}`,
		);
		if (response.ok) {
			const newToken = response.headers.get("Authorization");
			if (newToken) {
				localStorage.setItem("accessToken", newToken.split(" ")[1]); // Save the new token
			}
		}
		// console.log(response.data.result);
		return response.data.result;
	};
	const [isChatListModalOpen, setIsChatListModalOpen] = useState(false);
	const handleOpenModal = () => {
		setIsChatListModalOpen(true);
	};
	const handleCloseModal = () => {
		setIsChatListModalOpen(false);
	};
	const {
		data: chats = {},
		isChatListLoading,
		chatListError,
	} = useQuery({
		queryKey: ["chatList", selectedPropertyId],
		queryFn: () => fetchChatList(baseUrl, selectedPropertyId, accessToken),
		staleTime: 0,
		cacheTime: 0,
	});
	useEffect(() => {
		if (chats) {
			setPropertyChats(chats);
		}
	}, [chats, setPropertyChats]);

	if (error) return <p>Error fetching guests.</p>;
	console.log("guests length", guests.length);
	console.log("guests unique", new Set(guests.map((g) => g._id)).size);

	return (
		<>
			{isLoading && guests.length === 0 && (
				<div className="flex justify-center py-10">
					<LoadingSpinner />
				</div>
			)}
			<div className="px- sm:px- lg:px-">
				<div className="sm:flex sm:items-center mt-2">
					<div className="flex items-center justify-between sm:flex-row">
						<div className="flex flex-col  sm:flex-row items-center">
							{/* date picker and date */}
							<div className="relative inline-block" ref={calendarRef}>
								<h1 className="text-base font-semibold text-gray-900 flex items-center space-x-2">
									<CiCalendarDate
										ref={calendarRef}
										className="text-gray-500 text-3xl cursor-pointer"
										onClick={() => setIsDatePickerOpen((open) => !open)}
									/>
									<span>
										{selectedDate
											? dayjs(selectedDate).format("MM/DD")
											: dayjs().format("MM/DD")}
									</span>
								</h1>
								{isDatePickerOpen && (
									<div className="absolute top-full left-0 mt-2">
										<DatePicker
											selected={
												selectedDate ? dayjs(selectedDate).toDate() : null
											}
											onChange={handleDateChange}
											inline
											className="border p-2 rounded-md"
											dateFormat="MM/dd/yyyy" // Changed to US format
										/>
									</div>
								)}
							</div>

							<div className="mt-2 text-sm sm:mt-0 sm:ml-2 flex space-x-2">
								{statuses.map((status) => (
									<button
										key={status}
										onClick={() => handleClick(status)}
										className={`px-4 py-2 rounded-lg font-semibold ${
											currentStatus === status
												? "bg-indigo-600 text-white"
												: " text-gray-700"
										} hover:bg-indigo-500 hover:text-white transition-colors`}
									>
										{status}
									</button>
								))}
							</div>
						</div>
					</div>

					{/* search */}
					<div className="flex flex-1 justify-center px-2 lg:ml-6 lg:justify-end">
						<div className="w-full max-w-lg lg:max-w-xs">
							<label htmlFor="search" className="sr-only">
								Search
							</label>
							<div className="relative text-gray-400 focus-within:text-gray-600">
								<div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
									<MagnifyingGlassIcon aria-hidden="true" className="h-5 w-5" />
								</div>
								<input
									id="search"
									name="search"
									type="search"
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
									placeholder="Search Guests"
									className="block w-full rounded-2xl border-0 bg-gray-100 py-1.5 pl-10 pr-3 text-gray-900 focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-indigo-600 sm:text-sm sm:leading-6"
								/>
							</div>
						</div>
					</div>

					<div className="flex items-center space-x-3 mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
						{/* 🔔 Notification Bell */}
						<button
							type="button"
							onClick={openAddonRequestModal}
							className="relative inline-flex items-center justify-center rounded-full bg-indigo-600 p-2 text-white shadow-sm hover:bg-indigo-500 hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-all duration-200"
						>
							{addOnLength > 0 && (
								<span className="absolute -top-1 -right-1 inline-flex items-center justify-center h-5 w-5 rounded-full bg-red-500 text-xs font-medium text-white">
									{addOnLength}
								</span>
							)}
							<BellIcon className="h-5 w-5" />
						</button>

						{/* 💬 Chat Button (NEXT TO BELL) */}
						<button
							onClick={handleOpenModal}
							className="relative inline-flex items-center justify-center rounded-full bg-indigo-600 p-2 text-white shadow-sm hover:bg-indigo-500 hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-all duration-200"
						>
							<BiSolidMessageRoundedDetail className="h-5 w-5" />

							{chats?.totalUnreadMessages > 0 && (
								<span className="absolute -top-1 -right-1 inline-flex items-center justify-center h-5 w-5 rounded-full bg-yellow-500 text-xs font-medium text-white">
									{chats.totalUnreadMessages}
								</span>
							)}
						</button>

						{/* ➕ New Booking */}
						<button
							type="button"
							onClick={() => setIsModalOpen(true)}
							className="inline-flex items-center gap-x-1 rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
						>
							<CiCirclePlus className="h-5 w-5" />
							New Booking
						</button>
					</div>
				</div>
				{isChatListModalOpen && (
					<ChatListModal onClose={handleCloseModal} chats={chats} />
				)}
				{(currentStatus === "Check In" || currentStatus === "Check Out") &&
					pendingCheckInOutGuestsData &&
					pendingCheckInOutGuestsData.length > 0 && (
						<>
							<div className="mt-4 flex justify-center items-center gap-2 w-full mx-auto bg-[#FDA4AF] p-1">
								<div
									className="flex justify-center items-center gap-2 cursor-pointer"
									onClick={() =>
										setShowCheckInPendingGuests(!showCheckInPendingGuests)
									}
								>
									{showCheckInPendingGuests ? (
										<>
											{pendingCheckInOutGuestsData.length}
											{currentStatus === "Check In"
												? ` Check in Pending `
												: ` Check out Pending `}
											<span className="text-blue-600">Collapse</span>{" "}
											<GoChevronUp />
										</>
									) : (
										<>
											{pendingCheckInOutGuestsData.length}
											{currentStatus === "Check In"
												? ` Check in Pending `
												: ` Check out Pending `}
											<span className="text-blue-600">Expand</span>{" "}
											<GoChevronDown />
										</>
									)}
								</div>
							</div>
							{showCheckInPendingGuests && (
								<div className="my-4 flow-root">
									<div className="-mx-4 -my-2 max-h-screen overflow-x-auto sm:-mx-6 lg:-mx-6">
										<div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
											<table className="table-fixed min-w-full divide-y divide-gray-300">
												<thead>
													<tr className="bg-[#F9FAFB]">
														<th
															scope="col"
															className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0"
														>
															Name
														</th>
														<th
															scope="col"
															className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
														>
															Status
														</th>
														<th
															scope="col"
															className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
														>
															Check In/Out
														</th>
														{!(mapStatus[currentStatus] === "Reservation") && (
															<th
																scope="col"
																className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
															>
																Room No
															</th>
														)}

														<th
															scope="col"
															className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
														>
															Source
														</th>
														<th
															scope="col"
															className="w-[220px] max-w-[220px] px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
														>
															Details
														</th>
														<th className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">
															{/* Bell icon column */}
														</th>
													</tr>
												</thead>
												<tbody className="divide-y divide-gray-200">
													{pendingCheckInOutGuestsData.length > 0 &&
														pendingCheckInOutGuestsData.map((guest) => (
															<tr
																key={guest._id}
																onClick={() => {
																	setIsChangeStatusModalOpen(true);
																	setGuest(guest);
																}}
															>
																<td className="whitespace-nowrap py-5 pl-4 pr-3 text-sm sm:pl-0">
																	<div className="flex items-center">
																		<div
																			className={`h-10 w-10 flex-shrink-0 flex items-center justify-center rounded-full ${
																				guest.status.preArrivalStatus ===
																				"Applied"
																					? "bg-green-500"
																					: "bg-[#6B7280]"
																			} text-white font-medium`}
																		>
																			{guest.status.preArrivalStatus ===
																			"Applied" ? (
																				<span className="text-xl">✓</span> // Check mark symbol
																			) : (
																				getInitials(
																					guest.firstName,
																					guest.lastName,
																				) // Function to get initials
																			)}
																		</div>

																		<div className="ml-4">
																			<div className=" flex flex-col">
																				<div className="font-medium text-gray-900">
																					{guest.firstName +
																						" " +
																						guest.lastName}
																				</div>

																				<div
																					className={`font-medium text-gray-700 inline-flex items-center rounded-md px-2 py-1 text-xs ring-1 ring-inset
                                    "text-gray-700 bg-gray-50 ring-gray-600/20`}
																				>
																					{convertToStatus(guest.status)}
																					<span className="ml-1 w-14">
																						{guest.confirmationNumber &&
																						guest.confirmationNumber.length > 4
																							? `${guest.confirmationNumber.slice(
																									0,
																									4,
																								)}...`
																							: guest.confirmationNumber}
																					</span>
																				</div>
																			</div>
																		</div>
																	</div>
																</td>

																{/* Guest status */}
																<td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
																	{(() => {
																		const {
																			reservationStatus,
																			earlyCheckInStatus,
																			lateCheckOutStatus,
																		} = guest.status;

																		// Function to determine status label and color
																		const getStatusDetails = () => {
																			if (reservationStatus === "Confirmed") {
																				const earlyCheckInLabel =
																					earlyCheckInStatus === "Requested"
																						? "Early Check-In Requested"
																						: earlyCheckInStatus === "Accepted"
																							? "Early Check-In Accepted"
																							: earlyCheckInStatus ===
																									"Declined"
																								? "Early Check-In Declined"
																								: "No Early Check-In";

																				const lateCheckOutLabel =
																					lateCheckOutStatus === "Requested"
																						? "Late Check-Out Requested"
																						: lateCheckOutStatus === "Accepted"
																							? "Late Check-Out Accepted"
																							: lateCheckOutStatus ===
																									"Declined"
																								? "Late Check-Out Declined"
																								: "No Late Check-Out";

																				return [
																					{
																						label: earlyCheckInLabel,
																						color:
																							earlyCheckInStatus === "Requested"
																								? "text-yellow-700 bg-yellow-50 ring-yellow-600/20"
																								: earlyCheckInStatus ===
																										"Accepted"
																									? "text-green-700 bg-green-50 ring-green-600/20"
																									: earlyCheckInStatus ===
																											"Declined"
																										? "text-red-700 bg-red-50 ring-red-600/20"
																										: "text-gray-700 bg-gray-50 ring-gray-600/20",
																					},
																					{
																						label: lateCheckOutLabel,
																						color:
																							lateCheckOutStatus === "Requested"
																								? "text-yellow-700 bg-yellow-50 ring-yellow-600/20"
																								: lateCheckOutStatus ===
																										"Accepted"
																									? "text-green-700 bg-green-50 ring-green-600/20"
																									: lateCheckOutStatus ===
																											"Declined"
																										? "text-red-700 bg-red-50 ring-red-600/20"
																										: "text-gray-700 bg-gray-50 ring-gray-600/20",
																					},
																				];
																			} else if (
																				reservationStatus === "Cancelled"
																			) {
																				return {
																					label: "No Show",
																					color:
																						"text-red-700 bg-red-50 ring-red-600/20",
																				};
																			}

																			// Optionally return null or an empty array/object if none of the conditions match
																			return null;
																		};

																		// Get the status details
																		const statusDetails = getStatusDetails();

																		return Array.isArray(statusDetails) ? (
																			statusDetails.map((status, index) => (
																				<div key={index} className="block mb-1">
																					<span
																						className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${
																							status.color ||
																							"text-gray-700 bg-gray-50 ring-gray-600/20"
																						}`}
																					>
																						{status.label || "Unknown Status"}
																					</span>
																				</div>
																			))
																		) : (
																			<div className="block">
																				<span
																					className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${
																						statusDetails?.color ||
																						"text-gray-700 bg-gray-50 ring-gray-600/20"
																					}`}
																				>
																					{statusDetails?.label ||
																						"Unknown Status"}
																				</span>
																			</div>
																		);
																	})()}
																</td>

																<td className="whitespace-nowrap px-3 py-5 space-y-1 text-sm text-gray-500">
																	<div className="flex items-center space-x-2">
																		<img
																			src={checkInI}
																			alt="Check-in"
																			className="h-4 w-4"
																		/>
																		<p>
																			{" "}
																			{/* {formatDateWithLocalTimezone(guest.checkIn)} */}
																			{new Date(guest.checkIn).toLocaleString(
																				"en",
																				{
																					day: "2-digit",
																					month: "2-digit",
																					hour: "2-digit",
																					minute: "2-digit",
																					hour12: true,
																					timeZone: "UTC",
																				},
																			)}
																		</p>
																	</div>

																	<div className="flex items-center space-x-2">
																		<img
																			src={checkOutI}
																			alt="Check-out"
																			className="h-3.5 w-3.5"
																		/>
																		<p>
																			{" "}
																			{/* {formatDateWithLocalTimezone(guest.checkOut)} */}
																			{new Date(guest.checkOut).toLocaleString(
																				"en",
																				{
																					day: "2-digit",
																					month: "2-digit",
																					hour: "2-digit",
																					minute: "2-digit",
																					hour12: true,

																					timeZone: "UTC",
																				},
																			)}
																		</p>
																	</div>
																</td>

																{!(
																	mapStatus[currentStatus] === "Reservation"
																) && (
																	<td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
																		{guest.roomNumber}
																	</td>
																)}

																<td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
																	{guest.source}
																</td>
																<td className="relative w-[220px] max-w-[220px] py-5 pl-3 pr-4 text-sm font-medium sm:pr-0 align-top">
																	<div className="flex justify-between gap-2 min-w-0">
																		<div className="flex min-w-0 flex-1 flex-col space-y-2 overflow-hidden">
																			<div className="truncate text-gray-500">
																				<span className="font-semibold"></span>

																				{guest.phoneNumber ? (
																					<>
																						{guest.countryCode}{" "}
																						{guest.phoneNumber}
																					</>
																				) : (
																					<span className="text-red-500">
																						No phone number
																					</span>
																				)}
																			</div>
																			<div className="group relative min-w-0 cursor-default text-gray-500">
																				<span
																					className="block truncate"
																					title={
																						guest.email?.length > 30
																							? guest.email
																							: undefined
																					}
																				>
																					{guest.email || "No email"}
																				</span>
																				{guest.email?.length > 30 && (
																					<div className="absolute left-0 top-full z-50 mt-1 hidden max-w-xs rounded bg-gray-800 px-2 py-1 text-xs text-white shadow-lg group-hover:block whitespace-normal">
																						{guest.email}
																					</div>
																				)}
																			</div>
																		</div>

																		<div className="flex flex-shrink-0 space-x-2">
																			{/* <button className="text-indigo-600 hover:text-indigo-900 text-xl">
                              <img src={Edit} alt="Edit" className="w-5 h-5" />
                            </button> */}
																			<button
																				className="text-red-600 hover:text-red-900 text-xl"
																				onClick={(e) => {
																					e.stopPropagation();
																					setIsChangeStatusModalOpen(true);
																					setGuest(guest);
																				}}
																			>
																				<img
																					src={ChangeStats}
																					alt="Delete"
																					className="w-5 h-5"
																				/>
																			</button>

																			<button
																				className="text-indigo-600 hover:text-indigo-900 text-xl"
																				onClick={(e) => {
																					e.stopPropagation();
																					openUpdateGuestModal(guest);
																					setGuest(guest);
																				}} // Open the Update Guest modal with guest ID
																			>
																				<img
																					src={Edit}
																					alt="Edit"
																					className="w-5 h-5"
																				/>
																			</button>

																			<button
																				className="text-blue-600 hover:text-blue-900 text-xl"
																				onClick={(e) => {
																					e.stopPropagation();
																					openChatScreen(guest);
																				}}
																			>
																				<img
																					src={MessageIcon}
																					alt="View"
																					className="w-5 h-5"
																				/>
																			</button>
																		</div>
																	</div>
																</td>
																<td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500 text-right align-middle">
																	{addOns.length > 0 &&
																		addOns.some(
																			(addon) =>
																				String(addon?.guestId?._id) ===
																					String(guest?._id) &&
																				addon?.requestStatus === "Requested",
																		) && (
																			<button
																				onClick={openAddonRequestModal}
																				className="group relative inline-flex items-center justify-center p-2 rounded-full hover:bg-red-50 hover:scale-110 transition-all duration-200 ease-in-out transform hover:rotate-12"
																				title="View Add-on Requests"
																			>
																				<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75 group-hover:bg-red-300 group-hover:animate-pulse"></span>
																				<BellIcon
																					className="relative h-6 w-6 text-red-600 group-hover:text-red-700 transition-colors duration-200"
																					title="Add-on Requested"
																				/>
																				<span className="absolute -top-1 -right-1 inline-flex items-center justify-center h-4 w-4 rounded-full bg-red-500 text-xs font-bold text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200">
																					!
																				</span>
																			</button>
																		)}
																</td>
															</tr>
														))}
												</tbody>
											</table>
										</div>
									</div>
								</div>
							)}
						</>
					)}

				{/* Table of propeties diplay here */}
				<div className="mt-4 flow-root">
					<div className="sm:mx-0 lg:mx-0">
						<div className="-mx-2 -my-2 h-[75vh] overflow-y-auto overflow-x-auto sm:-mx-6 lg:-mx-6">
							<div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:pl-2 lg:pr-0">
								<table className="table-fixed min-w-full divide-y divide-gray-300">
									<thead className="sticky top-0 z-10 bg-[#F9FAFB]">
										<tr className="bg-[#F9FAFB]">
											<th
												scope="col"
												className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 "
											>
												Name
											</th>
											<th
												scope="col"
												className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
											>
												Status
											</th>
											<th
												scope="col"
												className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
											>
												Check In/Out
											</th>
											{!(mapStatus[currentStatus] === "Reservation") && (
												<th
													scope="col"
													className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
												>
													Room No
												</th>
											)}

											<th
												scope="col"
												className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
											>
												Source
											</th>
											<th
												scope="col"
												className="w-[220px] max-w-[220px] px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
											>
												Details
											</th>
											<th className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">
												{/* Bell icon column */}
											</th>
										</tr>
									</thead>

									<tbody className="divide-y divide-gray-200 bg-white">
										{guests.length
											? guests.map((guest, index) => (
													<tr
														ref={
															index === guests.length - 1 ? lastGuestRef : null
														}
														key={guest._id}
														onClick={
															guest?.isReview
																? undefined
																: () => {
																		setIsChangeStatusModalOpen(true);
																		setGuest(guest);
																	}
														}
													>
														<td className="whitespace-nowrap py-5 pl-4 pr-3 text-sm ">
															<div className="flex items-center">
																{/* <div className="h-10 w-10 flex-shrink-0 flex items-center justify-center rounded-full bg-[#6B7280] text-white font-medium">
                            {getInitials(guest.firstName, guest.lastName)}
                          </div> */}
																<div
																	className={`h-10 w-10 flex-shrink-0 flex items-center justify-center rounded-full ${
																		guest.status.preArrivalStatus === "Applied"
																			? "bg-green-500"
																			: "bg-[#6B7280]"
																	} text-white font-medium`}
																>
																	{guest.status.preArrivalStatus ===
																	"Applied" ? (
																		<span className="text-xl">✓</span> // Check mark symbol
																	) : (
																		getInitials(guest.firstName, guest.lastName) // Function to get initials
																	)}
																</div>
																<div className="ml-4 flex items-center gap-2">
																	<div className="flex flex-col">
																		<div className="font-medium text-gray-900 flex items-center gap-1">
																			{guest.firstName + " " + guest.lastName}

																			{/* {addOns.length > 0 &&
                                  addOns.some(
                                    (addon) =>
                                      String(addon.guestId._id) ===
                                        String(guest._id) &&
                                      addon.requestStatus === "Requested"
                                  ) && (
                                    <BellIcon
                                      className="h-5 w-5 text-yellow-500"
                                      title="Add-on Requested"
                                    />
                                  )} */}
																		</div>
																		<div className="block">
																			{!guest?.isReview && (
																				<span className="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset text-gray-700 bg-gray-50 ring-gray-600/20">
																					{convertToStatus(guest.status)}
																					{guest.confirmationNumber && (
																						<span className="ml-1 w-14">
																							{guest.confirmationNumber.length >
																							4
																								? `${guest.confirmationNumber.slice(
																										0,
																										4,
																									)}...`
																								: guest.confirmationNumber}
																						</span>
																					)}
																				</span>
																			)}
																		</div>

																		{/* {currentStatus === "All Bookings" && (
                                <div
                                  className="font-medium text-gray-700 inline-flex items-center rounded-md px-2 py-1 text-xs ring-1 ring-inset
                text-gray-700 bg-gray-50 ring-gray-600/20"
                                >
                                  {guest.status.currentStatus}
                                </div>
                              )} */}
																	</div>
																</div>
															</div>
														</td>

														{/* Guest status */}
														{guest?.isReview ? (
															<td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
																<div className="flex items-center gap-2">
																	<span className="text-sm text-gray-700 font-semibold ">
																		{Number(guest?.review?.overAllRating)}
																	</span>{" "}
																	{renderStars(guest?.review?.overAllRating)}
																</div>
															</td>
														) : (
															<td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
																{(() => {
																	const {
																		reservationStatus,
																		earlyCheckInStatus,
																		lateCheckOutStatus,
																	} = guest.status;

																	// Function to determine status label and color
																	const getStatusDetails = () => {
																		if (reservationStatus === "Confirmed") {
																			const earlyCheckInLabel =
																				earlyCheckInStatus === "Requested"
																					? "Early Check-In Requested"
																					: earlyCheckInStatus === "Accepted"
																						? "Early Check-In Accepted"
																						: earlyCheckInStatus === "Declined"
																							? "Early Check-In Declined"
																							: "No Early Check-In";

																			const lateCheckOutLabel =
																				lateCheckOutStatus === "Requested"
																					? "Late Check-Out Requested"
																					: lateCheckOutStatus === "Accepted"
																						? "Late Check-Out Accepted"
																						: lateCheckOutStatus === "Declined"
																							? "Late Check-Out Declined"
																							: "No Late Check-Out";

																			return [
																				{
																					label: earlyCheckInLabel,
																					color:
																						earlyCheckInStatus === "Requested"
																							? "text-yellow-700 bg-yellow-50 ring-yellow-600/20"
																							: earlyCheckInStatus ===
																									"Accepted"
																								? "text-green-700 bg-green-50 ring-green-600/20"
																								: earlyCheckInStatus ===
																										"Declined"
																									? "text-red-700 bg-red-50 ring-red-600/20"
																									: "text-gray-700 bg-gray-50 ring-gray-600/20",
																				},
																				{
																					label: lateCheckOutLabel,
																					color:
																						lateCheckOutStatus === "Requested"
																							? "text-yellow-700 bg-yellow-50 ring-yellow-600/20"
																							: lateCheckOutStatus ===
																									"Accepted"
																								? "text-green-700 bg-green-50 ring-green-600/20"
																								: lateCheckOutStatus ===
																										"Declined"
																									? "text-red-700 bg-red-50 ring-red-600/20"
																									: "text-gray-700 bg-gray-50 ring-gray-600/20",
																				},
																			];
																		} else if (
																			reservationStatus === "Cancelled"
																		) {
																			return {
																				label: "Reservation Cancelled",
																				color:
																					"text-red-700 bg-red-50 ring-red-600/20",
																			};
																		} else if (
																			reservationStatus === "No Show"
																		) {
																			return {
																				label: "No Show",
																				color:
																					"text-red-700 bg-red-50 ring-red-600/20",
																			};
																		}

																		// Optionally return null or an empty array/object if none of the conditions match
																		return null;
																	};

																	// Get the status details
																	const statusDetails = getStatusDetails();

																	// Render status with proper color and label
																	// return Array.isArray(statusDetails) ? (
																	//   statusDetails.map((status, index) => (
																	//     <div key={index} className="block mb-1">
																	//       <span
																	//         className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${status.color}`}
																	//       >
																	//         {status.label}
																	//       </span>
																	//     </div>
																	//   ))
																	// ) : (
																	//   <div className="block">
																	//     <span
																	//       className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${statusDetails.color}`}
																	//     >
																	//       {statusDetails.label}
																	//     </span>
																	//   </div>
																	// );
																	return Array.isArray(statusDetails) ? (
																		statusDetails.map((status, index) => (
																			<div key={index} className="block mb-1">
																				<span
																					className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${
																						status.color ||
																						"text-gray-700 bg-gray-50 ring-gray-600/20"
																					}`}
																				>
																					{status.label || "Unknown Status"}
																				</span>
																			</div>
																		))
																	) : (
																		<div className="block">
																			<span
																				className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${
																					statusDetails?.color ||
																					"text-gray-700 bg-gray-50 ring-gray-600/20"
																				}`}
																			>
																				{statusDetails?.label ||
																					"Unknown Status"}
																			</span>
																		</div>
																	);
																})()}
															</td>
														)}
														{guest?.isReview ? (
															<td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
																<p>
																	{" "}
																	{/* {formatDateWithLocalTimezone(guest.checkOut)} */}
																	{new Date(guest.checkOut).toLocaleString(
																		"en",
																		{
																			day: "2-digit",
																			month: "2-digit",
																			hour: "2-digit",
																			minute: "2-digit",
																			hour12: true,

																			timeZone: "UTC",
																		},
																	)}
																</p>
															</td>
														) : (
															<td className="whitespace-nowrap px-3 py-5 space-y-1 text-sm text-gray-500">
																<div className="flex items-center space-x-2">
																	<img
																		src={checkInI}
																		alt="Check-in"
																		className="h-4 w-4"
																	/>
																	<p>
																		{" "}
																		{/* {formatDateWithLocalTimezone(guest.checkIn)} */}
																		{new Date(guest.checkIn).toLocaleString(
																			"en",
																			{
																				day: "2-digit",
																				month: "2-digit",
																				hour: "2-digit",
																				minute: "2-digit",
																				hour12: true,
																				timeZone: "UTC",
																			},
																		)}
																	</p>
																</div>

																<div className="flex items-center space-x-2">
																	<img
																		src={checkOutI}
																		alt="Check-out"
																		className="h-3.5 w-3.5"
																	/>
																	<p>
																		{" "}
																		{/* {formatDateWithLocalTimezone(guest.checkOut)} */}
																		{new Date(guest.checkOut).toLocaleString(
																			"en",
																			{
																				day: "2-digit",
																				month: "2-digit",
																				hour: "2-digit",
																				minute: "2-digit",
																				hour12: true,

																				timeZone: "UTC",
																			},
																		)}
																	</p>
																</div>
															</td>
														)}

														{!(mapStatus[currentStatus] === "Reservation") && (
															<td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
																{guest.roomNumber}
															</td>
														)}

														<td className="whitespace-normal px-3 py-5 text-sm text-gray-500">
															<div className="break-words max-w-[15ch]">
																{guest.source}
															</div>
														</td>

														<td className="relative w-[220px] max-w-[220px] py-5 pl-3 pr-4 text-sm font-medium sm:pr-0 align-top">
															<div className="flex min-w-0 flex-col space-y-2">
																{/* Phone Number Section */}
																<div className="truncate text-gray-500">
																	<span className="font-semibold"></span>
																	{guest.phoneNumber ? (
																		<>
																			{guest.countryCode} {guest.phoneNumber}
																		</>
																	) : (
																		<span className="text-red-500">
																			No phone number
																		</span>
																	)}
																</div>

																{/* Email — truncate within fixed column; full value on hover */}
																<div className="group relative min-w-0 cursor-default text-gray-500">
																	<span
																		className="block truncate"
																		title={
																			guest.email?.length > 30
																				? guest.email
																				: undefined
																		}
																	>
																		{guest.email || "No email"}
																	</span>
																	{guest.email?.length > 30 && (
																		<div className="absolute left-0 top-full z-50 mt-1 hidden max-w-xs rounded bg-gray-800 px-2 py-1 text-xs text-white shadow-lg group-hover:block whitespace-normal">
																			{guest.email}
																		</div>
																	)}
																</div>
															</div>
														</td>
														{guest?.isReview ? (
															<td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500"></td>
														) : (
															<td className="sticky right-0 bg-white py-5 pl-3 pr-4 text-sm font-medium sm:pr-0 z-5 min-w-[120px]">
																<div className="flex justify-between pr-4">
																	{/* Change Status Button */}
																	<button
																		className="text-red-600 hover:text-red-900 text-xl"
																		onClick={(e) => {
																			e.stopPropagation();
																			setIsChangeStatusModalOpen(true);
																			setGuest(guest);
																		}}
																	>
																		<img
																			src={ChangeStats}
																			alt="Delete"
																			className="w-5 h-5"
																		/>
																	</button>

																	{/* Update Guest Button */}
																	<button
																		className="text-indigo-600 hover:text-indigo-900 text-xl"
																		onClick={(e) => {
																			e.stopPropagation();
																			openUpdateGuestModal(guest);
																			setGuest(guest);
																		}}
																	>
																		<img
																			src={Edit}
																			alt="Edit"
																			className="w-5 h-5"
																		/>
																	</button>

																	{/* Chat Button with Unread Messages Badge */}
																	<button
																		className="relative text-blue-600 hover:text-blue-900 text-xl"
																		onClick={(e) => {
																			e.stopPropagation();
																			openChatScreen(guest);
																		}}
																	>
																		{propertyChats?.chatLists?.find(
																			(chat) =>
																				chat.guestId === guest._id &&
																				chat.unreadMessages > 0,
																		) ? (
																			<MdOutlineMarkUnreadChatAlt
																				color="#4f46e5"
																				size={22}
																			/>
																		) : (
																			<MdOutlineMessage
																				color="#4f46e5"
																				size={22}
																			/>
																		)}
																		{/* Badge */}
																		{propertyChats?.chatLists?.find(
																			(chat) =>
																				chat.guestId === guest._id &&
																				chat.unreadMessages > 0,
																		)?.unreadMessages && (
																			<span className="absolute top-0 right-0 bg-red-600 text-white text-xs font-bold px-1 py-0.2 rounded-full">
																				{
																					propertyChats?.chatLists?.find(
																						(chat) =>
																							chat.guestId === guest._id &&
																							chat.unreadMessages > 0,
																					).unreadMessages
																				}
																			</span>
																		)}
																	</button>
																</div>
															</td>
														)}
														<td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500 text-right align-middle">
															{addOns.length > 0 &&
																addOns.some(
																	(addon) =>
																		String(addon?.guestId?._id) ===
																			String(guest?._id) &&
																		addon?.requestStatus === "Requested",
																) &&
																guest.status.currentStatus !== "Checked Out" &&
																guest.status.reservationStatus !==
																	"Cancelled" && (
																	<button
																		onClick={openAddonRequestModal}
																		className="group relative inline-flex items-center justify-center p-2 rounded-full hover:bg-red-50 hover:scale-110 transition-all duration-200 ease-in-out transform hover:rotate-12"
																		title="View Add-on Requests"
																	>
																		<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75 group-hover:bg-red-300 group-hover:animate-pulse"></span>
																		<BellIcon
																			className="relative h-6 w-6 text-red-600 group-hover:text-red-700 transition-colors duration-200"
																			title="Add-on Requested"
																		/>
																		<span className="absolute -top-1 -right-1 inline-flex items-center justify-center h-4 w-4 rounded-full bg-red-500 text-xs font-bold text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200">
																			!
																		</span>
																	</button>
																)}
														</td>
													</tr>
												))
											: !isLoading && (
													<tr>
														<td colSpan={7} className="py-10 text-center">
															<NoDataFound message={notFoundMessage} />
														</td>
													</tr>
												)}

										{/* ✅ LOADER ROW */}
										{isFetchingNextPage && (
											<tr>
												<td colSpan={7} className="py-6">
													<div className="flex justify-center">
														<LoadingSpinner fullScreen={false} />
													</div>
												</td>
											</tr>
										)}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
				{/* Add Guest Modal */}
				<AddGuestModal
					isOpen={isModalOpen}
					onClose={() => setIsModalOpen(false)}
				/>

				{/* Render the Update Guest Modal */}
				{isUpdateGuestModalOpen && (
					<UpdateGuestModal
						guest={guest}
						onClose={closeUpdateGuestModal} // Close modal function
					/>
				)}

				{guest && (
					<ChangeStatusModal
						isOpen={isChangeStatusModalOpen}
						onClose={() => setIsChangeStatusModalOpen(false)}
						guest={guest}
					/>
				)}

				{guest && (
					<EditGuestModal
						isOpen={isEditGuestModalOpen}
						onClose={() => setIsEditGuestModalOpen(false)}
						guest={guest}
					/>
				)}

				{selectedGuest && (
					<ChatScreenModal
						guestId={selectedGuest._id}
						guest={selectedGuest}
						onClose={closeChatScreen}
					/>
				)}

				{isAddonRequestModalOpen && (
					<AddonRequestModal
						propertyId={propertyId}
						onClose={(requestedCount) => {
							setAddOnLength(requestedCount);
							setIsAddonRequestModalOpen(false);
							fetchAddOns();
						}}
					/>
				)}
			</div>
		</>
	);
}
